import type { vitality as en } from '@/i18n/locales/en/vitality';
import type { Translation } from '@/i18n/types';

export const vitality: Translation<typeof en> = {
  page: {
    title: 'Vitalidade',
    description: 'Tendências de bem-estar anonimizadas e indicativas ao nível do grupo.',
    noScope: 'Selecione uma empresa para ver os seus números de vitalidade.',
    noDepartments: 'Nenhum departamento aqui tem estatísticas que possa ver.',
    disclaimer:
      'Todos os valores são <strong>indicações de bem-estar</strong> e <strong>não constituem um diagnóstico médico</strong>. As tendências só são mostradas com um número suficiente de participantes (privacidade desde a conceção).',
  },
  filters: {
    title: 'Filters',
    department: {
      label: 'Departamento',
    },
    location: {
      label: 'Localização',
      all: 'Todas as localizações',
    },
  },
  badge: {
    measured: 'Medido',
    aiDerived: 'Análise de IA',
  },
  level: {
    good: 'Bom',
    opportunity: 'Oportunidade',
  },
  statusPill: {
    healthy: 'Nível saudável',
    opportunity: 'Oportunidade de ajudar',
    strongForward: 'Forte e a subir',
  },
  trend: {
    flat: 'Sem alteração',
    vsPrevious: 'vs período anterior',
    vsAverage: 'vs média do período',
    up: 'A subir',
    down: 'Ligeiramente mais baixo',
    upBad: 'Ligeiramente mais alto',
    week: 'sem.',
  },
  units: {
    hoursMinutes: '{{h}}h {{m}}m',
    average: 'méd. {{value}}',
  },
  status: {
    noData: 'Sem dados neste período.',
  },
  table: {
    level: 'Nível',
    trend: 'Tendência',
    action: 'Ação',
    actionNone: '—',
    actionLink: 'Departamento',
    actionLinkTitle: 'Ir para este departamento',
  },
  insight: {
    title: 'O que dizem os números de vitalidade',
    unavailable: 'Ainda não há dados suficientes para um resumo.',
    vitality: {
      up: 'A vitalidade está a subir ({{pct}}% {{period}}) com uma taxa de participação de {{participation}}%.',
      down: 'A vitalidade está a descer ({{pct}}% {{period}}) com uma taxa de participação de {{participation}}%.',
      flat: 'A vitalidade está estável ({{pct}}% {{period}}) com uma taxa de participação de {{participation}}%.',
      noParticipation: 'A vitalidade está estável ({{pct}}% {{period}}).',
    },
    period: {
      last7Days: 'nos últimos 7 dias',
      last30Days: 'este mês',
      thisWeek: 'esta semana',
      thisMonth: 'este mês',
      custom: 'neste período',
    },
    stress: {
      up: 'O stress está a subir ({{pct}}%)',
      down: 'o stress está a diminuir ({{pct}}%)',
      flat: 'o stress mantém-se estável',
    },
    sleep: {
      longer: 'e os colaboradores dormem, em média, mais {{minutes}} min.',
      shorter: 'e os colaboradores dormem, em média, menos {{minutes}} min.',
      same: 'e os colaboradores dormem aproximadamente o mesmo.',
    },
    mostActiveDay: '{{day}} é o dia mais ativo.',
  },
  cards: {
    vitalityScore: {
      title: 'Pontuação de vitalidade',
      unit: '%',
      info: 'Uma pontuação composta com base no sono, atividade, VFC, stress e frequência cardíaca em repouso.',
    },
    sleep: {
      title: 'Sono',
      unit: '',
      info: 'Duração média do sono por noite.',
    },
    stress: {
      title: 'Stress',
      unit: '%',
      info: 'Nível de stress medido; quanto mais baixo, mais calmo.',
    },
    recovery: {
      title: 'Recuperação',
      unit: '%',
      info: 'O quão bem o corpo recupera, com base no sono, VFC e stress.',
    },
    activity: {
      title: 'Atividade',
      unit: 'passos/dia',
      info: 'Número médio de passos por dia.',
    },
    hrv: {
      title: 'VFC',
      unit: 'ms',
      info: 'Variabilidade da frequência cardíaca — um indicador de recuperação e resiliência.',
    },
    restingHeartRate: {
      title: 'Frequência cardíaca em repouso',
      unit: 'bpm',
      info: 'A frequência cardíaca mais baixa medida no período selecionado, como aproximação da frequência cardíaca em repouso.',
    },
  },
  actions: {
    vitalityScore: {
      good: 'A vitalidade está a evoluir bem — mantenha as rotinas atuais.',
      opportunity:
        'A vitalidade tem margem para crescer — uma curta conversa de equipa pode revelar o que está a travar.',
    },
    sleep: {
      good: 'O sono da equipa parece saudável — sem ação necessária.',
      opportunity:
        'O sono está abaixo do objetivo — considere um lembrete sobre uma rotina noturna e menos ecrãs antes de dormir.',
    },
    stress: {
      good: 'O nível de stress está controlado — continue a monitorizar.',
      opportunity:
        'O stress está elevado — uma curta conversa sobre a carga de trabalho ou uma atividade de recuperação pode ajudar.',
    },
    recovery: {
      good: 'A recuperação é sólida — a equipa recupera bem entre esforços.',
      opportunity:
        'A recuperação está atrasada — verifique se os dias de descanso e a carga de trabalho estão equilibrados.',
    },
    activity: {
      good: 'O nível de atividade é saudável — sem ação necessária.',
      opportunity:
        'A atividade está abaixo do objetivo — uma reunião a caminhar ou um desafio de passos pode ajudar.',
    },
    hrv: {
      good: 'A VFC parece saudável — sem ação necessária.',
      opportunity:
        'A VFC está mais baixa do que o esperado — isto costuma associar-se a stress ou sono fraco; vale a pena falar sobre isso em equipa.',
    },
    restingHeartRate: {
      good: 'A frequência cardíaca em repouso está numa faixa saudável.',
      opportunity:
        'A frequência cardíaca em repouso está elevada — combinada com stress ou menos sono, vale a pena assinalar.',
    },
  },
};
